import { z } from 'zod';

import authService from '@/services/auth/auth';

import { apiRoutes } from './config';

export enum QuestionTypesOptions {
  'Single' = 'SINGLE_CHOICE',
  'Multi' = 'MULTIPLE_CHOICE',
  'Open' = 'OPEN_QUESTION',
}

export const SkillSchema = z.object({
  id: z.number(),
  name: z.string(),
});
export type Skill = z.infer<typeof SkillSchema>;

const TestSkillSchema = z.object({
  skill: SkillSchema,
});

export const QuestionTypesSchema = z.enum([
  'SINGLE_CHOICE',
  'MULTIPLE_CHOICE',
  'OPEN_QUESTION',
]);

export type QuestionTypes = z.infer<typeof QuestionTypesSchema>;

export const TestSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  numberOfClosedQuestions: z.number(),
  numberOfOpenQuestions: z.number(),
  timeForCompletionInMinutes: z.number(),
  nextApproachAllowedAt: z.string(),
  technologies: z.array(TestSkillSchema),
});
export type Test = z.infer<typeof TestSchema>;

export const AnswerSchema = z.object({
  id: z.string(),
  body: z.string(),
  codeBlock: z.string().nullable(),
});

export type AnswerType = z.infer<typeof AnswerSchema>;

const QuestionInternalSchema = z.object({
  body: z.string(),
  type: QuestionTypesSchema,
  closedAnswers: z.array(AnswerSchema),
  openAnswers: z.array(AnswerSchema),
  codeBlock: z.string().nullable(),
  programmingLanguage: z.string().nullable(),
});

export type QuestionInternalType = z.infer<typeof QuestionInternalSchema>;

export const QuestionSchema = z.object({
  id: z.string(),
  question: QuestionInternalSchema,
  wasAnswered: z.boolean(),
});

export type QuestionType = z.infer<typeof QuestionSchema>;

export const StartedTestSchema = z.object({
  id: z.string(),
  canceled: z.boolean(),
  test: TestSchema,
  score: z.number(),
  testEndsAt: z.string(),
  numberOfClosedQuestions: z.number(),
  numberOfOpenQuestions: z.number(),
  questions: z.array(QuestionSchema),
});

export type StartedTest = z.infer<typeof StartedTestSchema>;

export const AttemptSchema = z.object({
  id: z.string(),
  test: TestSchema.nullable(),
  score: z.number(),
  testEndsAt: z.string(),
  numberOfClosedQuestions: z.number(),
  numberOfOpenQuestions: z.number(),
  canceled: z.boolean(),
  questions: z.array(QuestionSchema),
});

export type Attempt = z.infer<typeof AttemptSchema>;

export const getTest = async (uuid: string) => {
  const { data } = await authService.get(apiRoutes.tests.available.test(uuid));
  return TestSchema.parse(data);
};

export const startTest = async (uuid: string) => {
  const { data } = await authService.post(
    apiRoutes.tests.available.start(uuid)
  );

  return StartedTestSchema.parse(data);
};

export const cancelTest = async (testUuid: string) => {
  const { data } = await authService.post(
    apiRoutes.tests.started.cancel(testUuid)._root
  );
  return AttemptSchema.parse(data);
};

export const sendClosedAnswer = async (
  testUuid: string,
  questionId: string,
  answers: string[]
) => {
  const { data } = await authService.post(
    apiRoutes.tests.started.test(testUuid).questions.question(questionId).answer
      .closed,
    { answers }
  );
  return QuestionSchema.parse(data);
};

export const sendOpenAnswer = async (
  testUuid: string,
  questionId: string,
  body: string,
  codeBlock?: string
) => {
  const { data } = await authService.post(
    apiRoutes.tests.started.test(testUuid).questions.question(questionId).answer
      .open,
    { body, codeBlock }
  );

  return QuestionSchema.parse(data);
};

export const getTestResult = async (uuid: string) => {
  const { data } = await authService.get(
    apiRoutes.tests.started.test(uuid)._root
  );
  return StartedTestSchema.parse(data);
};
